import { useCallback, useState } from 'react';
import { useGridSetPosition } from 'pages/ScheduleV6/components/views/hooks/useGridSetPosition';

/**
 * Handles drag-and-drop behaviour for the v6 grid view.
 *
 * - moves a sheet between grid positions;
 * - opens a confirm dialog when dropping on an occupied cell;
 * - updates positions via Redux actions (reuses legacy useGridSetPosition).
 */
export const useV6GridInteractions = ({ gridCells, hasEditAccess }) => {
  const { gridSetPosition } = useGridSetPosition();
  const [modalInfo, setModalInfo] = useState(null);

  const isAncestor = (node, target) => {
    if (node === target) return false;
    // eslint-disable-next-line no-param-reassign
    while (node && node.parentNode) {
      if (node.parentNode === target) return true;
      node = node.parentNode;
    }
    return false;
  };

  const handleDrop = useCallback(
    (position) => async (e) => {
      e.preventDefault();
      if (!hasEditAccess) return;

      e.currentTarget.style.backgroundColor = '#fff';

      const resourceId = e.dataTransfer.getData('resourceId');
      if (resourceId) {
        // Do not handle resource drag here, only sheet moves.
        return;
      }

      const sheetId = e.dataTransfer.getData('sheetsId');
      if (!sheetId) return;

      const occupant = gridCells[position];
      const acceptorId = occupant ? occupant.sheet._id : null;

      if (acceptorId && acceptorId === sheetId) return;

      if (acceptorId) {
        setModalInfo({ sheetId, gridPosition: position, destinationSheetId: acceptorId });
      } else {
        await gridSetPosition({
          sheetId,
          gridPosition: position,
        });
      }
    },
    [gridCells, gridSetPosition, hasEditAccess]
  );

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
  }, []);

  const handleDragEnter = useCallback((e) => {
    const isResourceId = e.dataTransfer.types.includes('resourceid');
    if (isResourceId) return;
    e.currentTarget.style.backgroundColor = '#8ec6ff';
  }, []);

  const handleDragLeave = useCallback((e) => {
    if (!isAncestor(e.relatedTarget, e.currentTarget)) {
      e.currentTarget.style.backgroundColor = '#fff';
    }
  }, []);

  const closeModal = useCallback(() => setModalInfo(null), []);

  const swapEvents = useCallback(async () => {
    if (!modalInfo) return;
    await gridSetPosition(modalInfo);
    setModalInfo(null);
  }, [gridSetPosition, modalInfo]);

  const getCellHandlers = useCallback(
    (position) => ({
      onDrop: handleDrop(position),
      onDragOver: handleDragOver,
      onDragEnter: handleDragEnter,
      onDragLeave: handleDragLeave,
    }),
    [handleDrop, handleDragOver, handleDragEnter, handleDragLeave]
  );

  return {
    modalInfo,
    closeModal,
    swapEvents,
    getCellHandlers,
  };
};

export default useV6GridInteractions;

